import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp,
  Co2Outlined,
  PrecisionManufacturing,
  AutoAwesome,
  ProductionQuantityLimits,
  Build,
  CheckCircle,
  LocalShipping,
  Park,
  Analytics,
  SupportAgent,
  Speed,
  Security,
  Business,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  category: string;
}

const features: Feature[] = [
  {
    title: 'Advanced Textile Manufacturing',
    description: 'State-of-the-art textile production with cutting-edge technology and sustainable practices',
    icon: <ProductionQuantityLimits sx={{ fontSize: 50, color: '#6366f1' }} />,
    category: 'Manufacturing',
    features: [
      'Automated weaving and knitting systems',
      'Digital printing technology',
      'Sustainable material processing',
      'Custom fabric development',
      'Quality control at every stage',
      'Bulk production capabilities',
    ],
  },
  {
    title: 'Smart Furniture Production',
    description: 'Innovative furniture manufacturing combining technology with timeless design',
    icon: <Build sx={{ fontSize: 50, color: '#06b6d4' }} />,
    category: 'Manufacturing',
    features: [
      'Smart furniture integration',
      'Ergonomic design principles',
      'Custom furniture solutions',
      'Modular design systems',
      'Quality wood processing',
      'Finishing excellence',
    ],
  },
  {
    title: 'Precision Metalworks',
    description: 'Advanced metal fabrication and precision engineering services',
    icon: <PrecisionManufacturing sx={{ fontSize: 50, color: '#f59e0b' }} />,
    category: 'Manufacturing',
    features: [
      'CNC machining services',
      'Laser cutting technology',
      'Welding and fabrication',
      'Surface treatment options',
      'Custom metal solutions',
      'Quality certifications',
    ],
  },
  {
    title: 'Quality Assurance',
    description: 'Comprehensive quality control systems ensuring excellence in every product',
    icon: <CheckCircle sx={{ fontSize: 50, color: '#10b981' }} />,
    category: 'Quality',
    features: [
      'ISO certified processes',
      'In-process quality checks',
      'Final product testing',
      'Material verification',
      'Defect rate below 0.2%',
      'Third-party certifications',
    ],
  },
  {
    title: 'Supply Chain Excellence',
    description: 'Efficient supply chain management and logistics solutions',
    icon: <LocalShipping sx={{ fontSize: 50, color: '#8b5cf6' }} />,
    category: 'Logistics',
    features: [
      'Global sourcing networks',
      'Inventory optimization',
      'Just-in-time delivery',
      'Supply chain visibility',
      'Customs handling',
      'Multi-modal transportation',
    ],
  },
  {
    title: 'Custom Manufacturing',
    description: 'Tailored manufacturing solutions for unique client requirements',
    icon: <AutoAwesome sx={{ fontSize: 50, color: '#ef4444' }} />,
    category: 'Services',
    features: [
      'Custom product development',
      'Design collaboration',
      'Prototype development',
      'Small batch production',
      'Material customization',
      'Flexible production runs',
    ],
  },
  {
    title: 'Sustainable Practices',
    description: 'Eco-friendly manufacturing processes and environmental responsibility',
    icon: <Park sx={{ fontSize: 50, color: '#059669' }} />,
    category: 'Sustainability',
    features: [
      'Renewable energy usage',
      'Waste reduction programs',
      'Recycling initiatives',
      'Eco-friendly materials',
      'Carbon footprint reduction',
      'Green certifications',
    ],
  },
  {
    title: 'Technology Integration',
    description: 'Cutting-edge technology integration for modern manufacturing',
    icon: <Analytics sx={{ fontSize: 50, color: '#7c3aed' }} />,
    category: 'Technology',
    features: [
      'IoT-enabled production',
      'Real-time monitoring',
      'Predictive maintenance',
      'Digital twin technology',
      'Automation systems',
      'Data analytics',
    ],
  },
  {
    title: 'Customer Support',
    description: 'Comprehensive support and service throughout the manufacturing process',
    icon: <SupportAgent sx={{ fontSize: 50, color: '#dc2626' }} />,
    category: 'Support',
    features: [
      'Technical consultation',
      'Product training',
      'Installation support',
      'Maintenance services',
      'Warranty coverage',
      '24/7 support availability',
    ],
  },
  {
    title: 'Speed & Efficiency',
    description: 'Rapid production capabilities without compromising quality',
    icon: <Speed sx={{ fontSize: 50, color: '#ea580c' }} />,
    category: 'Performance',
    features: [
      'Fast turnaround times',
      'Efficient production lines',
      'Quick prototyping',
      'Rapid delivery',
      'Streamlined processes',
      'Performance optimization',
    ],
  },
  {
    title: 'Security & Compliance',
    description: 'Enterprise-grade security and regulatory compliance',
    icon: <Security sx={{ fontSize: 50, color: '#1f2937' }} />,
    category: 'Security',
    features: [
      'Data protection',
      'Intellectual property security',
      'Regulatory compliance',
      'Quality certifications',
      'Safety standards',
      'Audit trails',
    ],
  },
  {
    title: 'Business Solutions',
    description: 'Comprehensive business support and partnership opportunities',
    icon: <Business sx={{ fontSize: 50, color: '#374151' }} />,
    category: 'Business',
    features: [
      'Strategic partnerships',
      'Business consulting',
      'Market analysis',
      'Growth strategies',
      'Financial solutions',
      'Risk management',
    ],
  },
];

const categories = [
  { name: 'All Features', value: 'all', color: '#6366f1' },
  { name: 'Manufacturing', value: 'Manufacturing', color: '#06b6d4' },
  { name: 'Quality', value: 'Quality', color: '#10b981' },
  { name: 'Logistics', value: 'Logistics', color: '#8b5cf6' },
  { name: 'Services', value: 'Services', color: '#ef4444' },
  { name: 'Sustainability', value: 'Sustainability', color: '#059669' },
  { name: 'Technology', value: 'Technology', color: '#7c3aed' },
  { name: 'Support', value: 'Support', color: '#dc2626' },
  { name: 'Performance', value: 'Performance', color: '#ea580c' },
  { name: 'Security', value: 'Security', color: '#1f2937' },
  { name: 'Business', value: 'Business', color: '#374151' },
];

export const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredFeatures = selectedCategory === 'all' 
    ? features 
    : features.filter(feature => feature.category === selectedCategory);

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: 12,
      pb: 16,
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
          color: 'white',
          py: 12,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 4,
              textAlign: 'center',
            }}
          >
            Monio Manufacturing Features
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, textAlign: 'center' }}>
            Discover our comprehensive manufacturing capabilities and cutting-edge technology
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.8, textAlign: 'center' }}>
            From advanced textile manufacturing to smart furniture production, Monio combines traditional craftsmanship 
            with modern technology to deliver exceptional products and services.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Category Filter */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, textAlign: 'center' }}>
            Explore Our Capabilities
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            {categories.map((category) => (
              <Chip
                key={category.value}
                label={category.name}
                onClick={() => setSelectedCategory(category.value)}
                sx={{
                  background: selectedCategory === category.value 
                    ? `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`
                    : 'rgba(255, 255, 255, 0.1)',
                  color: selectedCategory === category.value ? 'white' : 'text.primary',
                  border: selectedCategory === category.value 
                    ? 'none' 
                    : `1px solid ${category.color}40`,
                  '&:hover': {
                    background: selectedCategory === category.value 
                      ? `linear-gradient(135deg, ${category.color}dd 0%, ${category.color}bb 100%)`
                      : `${category.color}20`,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {filteredFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {feature.icon}
                  </Box>
                  
                  <Chip 
                    label={feature.category} 
                    size="small" 
                    sx={{ 
                      mb: 2,
                      background: 'rgba(99, 102, 241, 0.1)',
                      color: '#6366f1',
                      fontWeight: 500,
                    }} 
                  />
                  
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  
                  <List dense>
                    {feature.features.map((featureItem, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={featureItem} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontSize: '0.875rem' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Experience Monio Manufacturing?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            Let's discuss how our manufacturing capabilities can help bring your vision to life.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
